/* ====================================================================================================================
 * FAYARD CROSS-SELL — « Complétez avec »
 * --------------------------------------------------------------------------------------------------------------------
 * Ce fichier ne contient volontairement aucune logique métier. Tout ce qui demande des données Shopify — appartenance
 * aux collections, disponibilité, présence au panier, priorité des règles — est résolu par Liquid au rendu de la
 * section, dans snippets/fayard-cross-sell.liquid.
 *
 * Il ne reste ici qu'une chose que Liquid ne peut pas savoir : dans quel ORDRE le client a ajouté ses produits.
 * D'où une pile d'identifiants produits en sessionStorage, et une simple révélation de la bonne règle.
 *
 * Conséquence directe : aucune requête réseau supplémentaire, et pas de scintillement. Au moment où Prestige
 * re-rend le tiroir après un ajout, la pile est déjà à jour et le serveur a déjà envoyé le bon volet.
 * ==================================================================================================================== */

(function () {
  'use strict';

  var STORAGE_KEY = 'fayard:cross-sell-stack';
  var MAX_STACK = 20;

  /* Durée de l'animation d'entrée du tiroir (Drawer.createEnterAnimationControls : 0,3 s), plus une marge. */
  var DRAWER_ENTER_MS = 320;

  /* État partagé entre les rendus successifs du volet : le tiroir a-t-il fini de s'ouvrir ? */
  var drawerSettled = false;
  var entranceTimer = null;

  /* ------------------------------------------------------------------------------------------------------------------
   * Pile des produits ajoutés
   * ---------------------------------------------------------------------------------------------------------------- */

  function readStack() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  }

  function pushToStack(productId) {
    var id = String(productId);

    try {
      /* Un produit ré-ajouté redevient le plus récent : on retire son ancienne position au lieu d'empiler un doublon. */
      var stack = readStack().filter(function (entry) {
        return entry !== id;
      });

      stack.push(id);

      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stack.slice(-MAX_STACK)));
    } catch (error) {
      /* Navigation privée ou stockage refusé : on retombe simplement sur la règle de repli calculée par Liquid. */
    }
  }

  /**
   * <product-form> diffuse variant:add sur le formulaire, avec bubbles, avant de diffuser cart:change. L'écoute est
   * posée au niveau du document et une seule fois, car elle doit fonctionner partout — page produit, achat rapide,
   * volet cross-sell — y compris quand aucun volet n'est rendu.
   *
   * Les coffrets personnalisés n'empruntent pas ce chemin : gift-box-builder.js diffuse directement cart:change.
   * Un coffret ne touche donc jamais la pile, sans qu'il soit nécessaire de l'exclure ici.
   */
  document.addEventListener('variant:add', function (event) {
    var items = event.detail && event.detail.items;

    if (!items || !items.length) return;

    items.forEach(function (item) {
      if (item && item.product_id) pushToStack(item.product_id);
    });
  });

  /* ------------------------------------------------------------------------------------------------------------------
   * Le volet
   * ---------------------------------------------------------------------------------------------------------------- */

  var FayardCrossSell = class extends HTMLElement {
    connectedCallback() {
      this.rules = Array.prototype.slice.call(this.querySelectorAll('[data-xsell-rule]'));
      this.targetRule = this.resolveRule();
      this.abortController = new AbortController();

      var drawer = this.closest('cart-drawer');

      /* La bande mobile vit dans le corps du tiroir : elle arrive avec lui, aucune orchestration n'est nécessaire. */
      if (!drawer || !this.classList.contains('fayard-xsell--desktop')) {
        this.reveal(this.targetRule, false);
        return;
      }

      /* Le volet latéral, lui, est peint dès que le tiroir passe en display:block, donc avant que celui-ci n'ait fini
         de glisser. On attend la fin de son animation d'entrée, et on se rétracte dès le début de sa sortie : le
         volet se lit alors comme une extension du panier, jamais comme un élément qui le devance. */
      var signal = this.abortController.signal;
      drawer.addEventListener('dialog:before-show', this.onDrawerOpening.bind(this), { signal: signal });
      drawer.addEventListener('dialog:after-show', this.onDrawerShown.bind(this), { signal: signal });
      drawer.addEventListener('dialog:before-hide', this.onDrawerHiding.bind(this), { signal: signal });

      if (drawerSettled) {
        /* Tiroir ouvert et stabilisé : c'est un re-rendu après un changement de panier. On montre sans rejouer
           l'entrée, sinon le volet s'animerait à chaque ajout ou suppression. */
        this.reveal(this.targetRule, false);
      } else if (drawer.open) {
        /* À l'ouverture, le tiroir se déplace dans le body, ce qui déconnecte puis reconnecte ses enfants : ce
           connectedCallback-ci est rejoué en pleine animation d'entrée, alors que l'attribut open est déjà posé.
           L'attribut ne dit donc pas si le tiroir est en place, d'où drawerSettled. */
        this.armEntrance();
      }
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    onDrawerOpening() {
      this.armEntrance();
    }

    /**
     * Prépare l'entrée du volet pour la fin de l'animation du tiroir. Le minuteur sert de filet : si l'événement
     * de fin d'animation tarde ou ne vient jamais, le volet apparaît quand même.
     */
    armEntrance() {
      drawerSettled = false;
      window.clearTimeout(entranceTimer);
      entranceTimer = window.setTimeout(this.onDrawerShown.bind(this), DRAWER_ENTER_MS);
    }

    onDrawerShown() {
      window.clearTimeout(entranceTimer);

      /* L'événement peut arriver en retard, après qu'une fermeture a commencé : ne pas ressortir le volet alors. */
      var drawer = this.closest('cart-drawer');
      if (drawer && !drawer.open) return;

      drawerSettled = true;
      this.reveal(this.targetRule, true);
    }

    onDrawerHiding() {
      window.clearTimeout(entranceTimer);
      drawerSettled = false;
      this.classList.remove('is-visible');
    }

    /**
     * Table « identifiant produit -> index de règle » écrite par Liquid, au format |123:0|456:-1|.
     * L'index -1 désigne un produit du panier ne correspondant à aucune règle.
     */
    get ruleMap() {
      var map = {};

      (this.getAttribute('data-rule-map') || '').split('|').forEach(function (entry) {
        if (!entry) return;

        var parts = entry.split(':');
        if (parts.length === 2) map[parts[0]] = parseInt(parts[1], 10);
      });

      return map;
    }

    /**
     * Détermine la règle à afficher, ou null pour ne rien afficher.
     *
     * On remonte la pile du plus récent au plus ancien et on s'arrête au premier produit présent dans la table.
     * Cette table ne contient que les produits actuellement au panier, hors sac cadeau : un produit supprimé ou un
     * sac cadeau en est absent, donc simplement enjambé, et c'est ainsi que la règle précédente revient d'elle-même.
     *
     * En revanche un produit présent au panier mais associé à -1 arrête la remontée : le dernier ajout réel ne
     * correspond à aucune règle, le volet doit disparaître plutôt que de ressortir une recommandation périmée.
     */
    resolveRule() {
      var map = this.ruleMap;
      var stack = readStack();

      for (var i = stack.length - 1; i >= 0; i--) {
        if (Object.prototype.hasOwnProperty.call(map, stack[i])) {
          return map[stack[i]];
        }
      }

      /* Pile vide : premier passage, nouvel onglet ou stockage indisponible. Liquid a préparé un repli, la dernière
         ligne du panier correspondant à une règle. C'est une approximation assumée de l'ordre d'ajout. */
      var fallback = parseInt(this.getAttribute('data-fallback-rule'), 10);

      return isNaN(fallback) ? null : fallback;
    }

    /**
     * @param {number|null} ruleIndex - la règle à montrer, ou null pour n'en montrer aucune
     * @param {boolean} animate - false pour arriver directement à l'état visible, sans rejouer l'entrée
     */
    reveal(ruleIndex, animate) {
      var target = null;

      if (ruleIndex !== null && ruleIndex >= 0) {
        target = this.querySelector('[data-xsell-rule="' + ruleIndex + '"]');
      }

      this.rules.forEach(function (rule) {
        rule.hidden = rule !== target;
      });

      if (!target) {
        this.hidden = true;
        return;
      }

      if (!animate) {
        this.classList.add('is-instant');
      }

      this.hidden = false;

      /* La transition a besoin d'un état de départ calculé : l'élément vient de passer de display:none à visible.
         Lire offsetWidth force ce calcul immédiatement. Un requestAnimationFrame ne conviendrait pas : il ne se
         déclenche pas dans un onglet en arrière-plan, et le volet resterait alors bloqué à opacité zéro. */
      void this.offsetWidth;
      this.classList.add('is-visible');

      if (!animate) {
        void this.offsetWidth;
        this.classList.remove('is-instant');
      }
    }
  };

  if (!window.customElements.get('fayard-cross-sell')) {
    window.customElements.define('fayard-cross-sell', FayardCrossSell);
  }
})();
